import { sdkLoggerInterface as log } from '../../../Logger/Logger';
/**
 * Digital Data Strategy class,
 * The class will be used as the standard Analytics strategy by the App Loader for capturing any analytics information, of the Shell or container does not provide and specific Analytics strategy to be used
 * The basis of this strategy is that all existing channels that use the analytics base their analytics information capture on populating the **window.digitalData** object.
 * This object is exposed by the ensighten term library provided by the analytics team based on the configurations agreed with the consuming channel.
 * Digital data strategy uses this to help update the data on the **window.digitalData** object.
 */
class DigitalDataStrategy {
    constructor() {
        /**
         * This property determines if the Analytics manager is used in an application that already has the Tag Management System (TMS) initiated
         */
        this.tmsEnabledEnv = false;
        this.initDefaultPage = () => {
            this.digitalData = {
                page: {
                    pageInfo: {
                        pageName: window.document.title,
                        site: window.location.href
                    }
                },
                components: {},
                event: [],
                user: {}
            };
        };
        /**
         * Warning displayed if the environment is enabled with a Tag Management System (TMS)
         */
        this.tmsWarning = () => {
            log.warn(`Tag Management System enabled in this env, the digitalData object is locked. 
            Please emit appropriate event using logEvent() method.`);
        };
        /**
         * If it is not a TMS system enabled system, this initializes the digitalData object
         * with the page information
         * @param {IDigitalDataPage} pageData Page object to be updated
         */
        this.initPage = async (pageData) => {
            if (this.tmsEnabledEnv) {
                this.tmsWarning();
            }
            else {
                this.digitalData = {
                    page: { pageInfo: pageData },
                    components: {},
                    event: []
                };
            }
            return Promise.resolve(true);
        };
        /**
         * If it is not a TMS system enabled system, this updates the digitalData object
         * with the changes to the page. These are useful in a SPA enabled with synthetic routing.
         * @param {IDigitalDataPage} pageData Page object to be updated
         */
        this.updatePage = (pageData) => {
            if (this.tmsEnabledEnv) {
                this.tmsWarning();
            }
            else {
                const { page: { pageInfo } } = this.digitalData;
                this.digitalData.page.pageInfo = { ...pageInfo, ...pageData };
            }
            return Promise.resolve(true);
        };
        /**
         * Event logger used for logging data on the Analytics/Tag Management System (TMS)
         * @param {IDigitalDataEvent} eventData details of the event being captured
         */
        this.logEvent = (eventData) => {
            let { event } = this.digitalData;
            // if the event array is not initialized, creates and assigns an event array
            if (!event) {
                event = [];
                this.digitalData.event = event;
            }
            event.push(eventData);
            return Promise.resolve(true);
        };
        /**
         * If it is not a TMS system enabled system, this updates the digitalData object
         * with the component loaded on to the page.
         * @param {IDigitalDataComponent} : Details of the component being loaded
         */
        this.logComponent = ({ data, componentName }) => {
            if (this.tmsEnabledEnv) {
                this.tmsWarning();
            }
            else {
                const { components } = this.digitalData;
                components[componentName] = data;
            }
            return Promise.resolve(true);
        };
        /**
         * If it is not a TMS system enabled system, this initialises the user data object
         * with the details provided of user.
         * @param {IDigitalDataUser} : Details of the user to be initalised with
         */
        this.initUser = (userData) => {
            if (this.tmsEnabledEnv) {
                this.tmsWarning();
            }
            else {
                this.digitalData.user = userData;
            }
            return Promise.resolve(true);
        };
        /**
         * If it is not a TMS system enabled system, this updates the user data object
         * with the details provided of user.
         * @param {IDigitalDataUser} : Details of the user to be updated
         */
        this.updateUser = (userData) => {
            if (this.tmsEnabledEnv) {
                this.tmsWarning();
            }
            else {
                const { user } = this.digitalData;
                this.digitalData.user = { ...user, ...userData };
            }
            return Promise.resolve(true);
        };
        this.tmsEnabledEnv = !!this.digitalData;
        if (!this.tmsEnabledEnv) {
            this.initDefaultPage();
        }
    }
    // eslint-disable-next-line class-methods-use-this
    get digitalData() {
        return window.digitalData;
    }
    // eslint-disable-next-line class-methods-use-this
    set digitalData(value) {
        window.digitalData = value;
    }
}
export { DigitalDataStrategy };
//# sourceMappingURL=index.js.map